import React, { useMemo } from "react";
import {
  MapPin, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { EventPopover } from "@/components/EventPopover";
import {
  ROLES,
  isStudioJob,
  getMinStaff,
  getTodayDate,
  extractVenue,
} from "@/utils/constants";

export const RotaCalendarView = ({
  calendarDate,
  onSetMonth,
  onPrevMonth,
  onNextMonth,
  onGoToToday,
  calendarJobsByDate,
  teamMembers,
  unavailableByDate,
  recentChangeDates,
  getDisplayTitle,
  onOpenJobSheet,
  sheetStatuses,
  editMode,
  onHideJob,
  onDeleteJob,
}) => {
  const todayDate = getTodayDate();
  const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const calendarGrid = useMemo(() => {
    const { year, month } = calendarDate;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    for (let i = 0; i < startDow; i++) {
      const d = new Date(year, month, 1 - startDow + i);
      days.push({ date: d, outside: true });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), outside: false });
    }
    while (days.length < 42) {
      const d = new Date(year, month + 1, days.length - startDow - lastDay.getDate() + 1);
      days.push({ date: d, outside: true });
    }
    return days;
  }, [calendarDate]);

  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden" data-testid="calendar-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-[#f9fafb]">
        <div className="flex items-center gap-2">
          <button onClick={onPrevMonth} className="p-1 rounded hover:bg-gray-200 transition-colors" data-testid="cal-prev">
            <ChevronLeft className="h-5 w-5 text-[#605e5e]" />
          </button>
          <button onClick={onNextMonth} className="p-1 rounded hover:bg-gray-200 transition-colors" data-testid="cal-next">
            <ChevronRight className="h-5 w-5 text-[#605e5e]" />
          </button>
          <div className="flex items-center gap-1 ml-2">
            <select
              value={calendarDate.month}
              onChange={(e) => onSetMonth(calendarDate.year, parseInt(e.target.value, 10))}
              className="font-heading text-base sm:text-lg text-[#605e5e] bg-transparent border border-transparent hover:border-[#e5e7eb] rounded px-1 py-0.5 cursor-pointer focus:outline-none focus:border-[#8DA399]"
              data-testid="cal-month-picker"
            >
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>
            <select
              value={calendarDate.year}
              onChange={(e) => onSetMonth(parseInt(e.target.value, 10), calendarDate.month)}
              className="font-heading text-base sm:text-lg text-[#605e5e] bg-transparent border border-transparent hover:border-[#e5e7eb] rounded px-1 py-0.5 cursor-pointer focus:outline-none focus:border-[#8DA399]"
              data-testid="cal-year-picker"
            >
              {Array.from({ length: 9 }, (_, i) => 2024 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <button onClick={onGoToToday} className="text-xs px-3 py-1 rounded border border-[#e5e7eb] text-[#605e5e] hover:bg-gray-100 transition-colors" data-testid="cal-today">
          Today
        </button>
      </div>

      <div className="grid grid-cols-7 border-b border-[#e5e7eb]">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="px-2 py-1.5 text-center text-xs font-medium text-[#8d8b8b] border-r last:border-r-0 border-[#e5e7eb]">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {calendarGrid.map((cell) => {
          const dateStr = formatDateStr(cell.date);
          const dayJobs = calendarJobsByDate[dateStr] || [];
          const isToday = dateStr === todayDate;
          const hasRecent = recentChangeDates.includes(dateStr);
          const dayUnavail = unavailableByDate[dateStr] || [];

          return (
            <div
              key={dateStr}
              className={`min-h-[100px] sm:min-h-[120px] border-r border-b border-[#e5e7eb] last:border-r-0 p-1 ${
                cell.outside ? 'bg-[#f9fafb]' : 'bg-white'
              } ${isToday ? 'ring-2 ring-inset ring-[#8DA399]' : ''}`}
              data-testid={`cal-day-${dateStr}`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className={`text-xs font-medium leading-5 w-5 h-5 flex items-center justify-center rounded-full ${
                  isToday ? 'bg-[#605e5e] text-white' : cell.outside ? 'text-[#c4c4c4]' : 'text-[#605e5e]'
                }`}>
                  {cell.date.getDate()}
                </span>
                <div className="flex gap-0.5">
                  {hasRecent && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" title="Recently changed"></span>}
                  {dayUnavail.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-400" title="Unavailable"></span>}
                </div>
              </div>
              {dayUnavail.length > 0 && !cell.outside && (
                <div className="flex flex-wrap gap-0.5 mb-0.5">
                  {dayUnavail.map((person) => (
                    <span key={person} className="text-[9px] leading-tight px-1 py-0 rounded bg-red-100 text-red-700 truncate max-w-full">
                      {person} off
                    </span>
                  ))}
                </div>
              )}
              <div className="space-y-0.5 overflow-y-auto max-h-[70px] sm:max-h-[85px]">
                {dayJobs.slice(0, 5).map(job => {
                  const minStaff = getMinStaff(job.event_name || job.title);
                  const assigned = ROLES.filter(r => job.assignments?.[r]).length;
                  const understaffed = minStaff > 0 && assigned < minStaff && !job.staffing_confirmed;
                  const members = ROLES.map(r => job.assignments?.[r]).filter(Boolean).map(id => teamMembers.find(m => m.id === id)?.name).filter(Boolean);
                  return (
                    <Popover key={job.id}>
                      <PopoverTrigger asChild>
                        <button
                          className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity ${
                            isStudioJob(job)
                              ? 'bg-violet-100 text-violet-800'
                              : understaffed
                                ? 'bg-red-100 text-red-800'
                                : minStaff > 0 && assigned >= minStaff
                                  ? 'bg-[#8DA399]/20 text-[#3d5a47]'
                                  : 'bg-gray-100 text-[#605e5e]'
                          }`}
                          data-testid={`cal-event-${job.id}`}
                        >
                          {getDisplayTitle(job)}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" side="right" align="start">
                        <EventPopover
                          job={job}
                          members={members}
                          teamMembers={teamMembers}
                          understaffed={understaffed}
                          assigned={assigned}
                          minStaff={minStaff}
                          dayUnavail={dayUnavail}
                          getDisplayTitle={getDisplayTitle}
                          onOpenJobSheet={onOpenJobSheet}
                          sheetStatus={sheetStatuses?.[job.id]}
                          editMode={editMode}
                          onHideJob={onHideJob}
                          onDeleteJob={onDeleteJob}
                        />
                      </PopoverContent>
                    </Popover>
                  );
                })}
                {dayJobs.length > 5 && (
                  <span className="text-[10px] text-[#8d8b8b] px-1">+{dayJobs.length - 5} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
